import { FC } from "react"
import { Formik } from "formik"
import { object, string } from "yup"
import { ContactFormDetails } from "../../../types"
import ManageContactFormContent from "../../../common/ui/config-admin/config-contact-form/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteContactFormConfigMutation,
  useFetchDefaultContactFormConfigQuery,
  useFindContactFormDetailsQuery,
  useUpdateOrCreateContactFormConfigMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useConfigEditPage } from "../use-config-edit-page"

const TABS = [
  { id: "general", label: "General" },
  { id: "fields", label: "Fields" }
]

const ContactFormSchema = object({
  name: string().required().min(1),
  config: object().required(),
  brand_id: string().required().min(1)
})

const ContactFormEditPage: FC = () => {
  const [updateOrCreate, saveResult] =
    useUpdateOrCreateContactFormConfigMutation()
  const [deleteContactForm, deleteResult] = useDeleteContactFormConfigMutation()

  const {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigateBack
  } = useConfigEditPage<ContactFormDetails>({
    tabs: TABS,
    tab: "contact-form",
    label: "contact form",
    saveResult,
    deleteResult
  })

  const { data: contactFormData, isLoading } = useFindContactFormDetailsQuery(
    { id: id ?? "" },
    { skip: isCreate || !id }
  )

  const { data: defaultData } = useFetchDefaultContactFormConfigQuery(
    {},
    { skip: !isCreate }
  )

  const raw = contactFormData ?? {
    name: "",
    id: "",
    brand_id: "",
    created: "",
    updated: "",
    ...(defaultData || { is_default: "0", config: {} as any })
  }

  const initialValues: ContactFormDetails = {
    ...raw,
    config: raw.config ?? ({} as any)
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (contactFormData?.id) deleteContactForm(contactFormData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={2} />
  }

  const pageTitle = isCreate
    ? "New contact form"
    : contactFormData?.name ?? "Contact form"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={ContactFormSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              allowEditName
              backLabel="Contact forms"
              namePlaceholder="Contact form name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.is_default === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigateBack(values.brand_id)}
              onSubmit={onSubmit}
              onBack={() => navigateBack(values.brand_id)}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageContactFormContent
                  values={values}
                  tab={tab}
                  setFormField={setFormField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default ContactFormEditPage
